/**
 * Guide editor client (Guide roadmap G5): the four Command Center proxy routes over the
 * Content API's guide write API (Curated-By-Blog docs/guides/GUIDE_SYSTEM_REFERENCE.md
 * section 4.2.1). Kept out of api-client.ts (a structure-budget file) on purpose.
 *
 * The editor's name is never sent: the backend derives it from the signed-in user.
 */

import type { GuideOp } from '../components/guide-editor/model';
import { apiClient, handleApiError } from './api-client';
import type { APIErrorResponse } from './api-client';
import type { AxiosError } from 'axios';

export interface GuideQaSummary {
  ok: boolean;
  rules?: string[];
  failures?: string[];
  warning_rules?: string[];
  warnings?: string[];
  chapters?: number;
  picks?: number;
  imaged?: number;
  words?: number;
}

export interface FailedOp {
  index: number;
  op: string | null;
  error: string;
}

export interface GuideEditResult {
  revision: string;
  contentHash: string;
  qa: GuideQaSummary;
  failedOps: FailedOp[];
  status: string;
  warnings: string[];
  /** Only for a published guide: per-path result of refreshing the live page. */
  revalidation?: Record<string, unknown>;
}

export interface GuideEditHistoryEntry {
  editor: string;
  ops: Array<{ op?: string; [key: string]: unknown }>;
  at: string;
  fromRevision: string | null;
}

export interface GuideEditHistory {
  slug: string;
  history: GuideEditHistoryEntry[];
}

export interface NewGuideResult {
  slug: string;
  title: string;
  status: string;
  revision: string;
  contentHash: string;
  qa: GuideQaSummary;
}

export interface PlaceCandidate {
  tripsby_id: string | null;
  google_place_id: string | null;
  name: string;
  displayAddress: string;
  /** A signed, site-relative `/api/place-photo/...` path, or null. */
  photoSrc: string | null;
  distanceKm: number | null;
}

export interface PlaceSearchResult {
  query: string;
  city: string;
  candidates: PlaceCandidate[];
  sources: { google?: string; ers?: string };
}

// Edits with place operations call Google Places and ERS, so they get longer than the
// client's default 30s; the backend proxy allows 90s.
const EDIT_TIMEOUT_MS = 100_000;
const SEARCH_TIMEOUT_MS = 50_000;

// The values `POST /guides` takes (it lower-cases and hyphenates). "Where to stay" is
// `hotels`. The first chapter is named "{kicker} in {city}", except `itinerary`: "Day 1".
export const GUIDE_CATEGORIES = [
  { value: 'restaurants', label: 'Restaurants' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'things-to-do', label: 'Things to do' },
  { value: 'hotels', label: 'Where to stay' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'off-the-beaten-path', label: 'Off the beaten path' },
  { value: 'itinerary', label: 'Itinerary' },
] as const;

export const guideEditorApi = {
  async editGuide(slug: string, data: { revision: string; ops: GuideOp[] }): Promise<GuideEditResult> {
    try {
      const response = await apiClient.post<GuideEditResult>(
        `/api/v1/guides/${encodeURIComponent(slug)}/edits`,
        data,
        { timeout: EDIT_TIMEOUT_MS }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getEditHistory(slug: string): Promise<GuideEditHistory> {
    try {
      const response = await apiClient.get<GuideEditHistory>(
        `/api/v1/guides/${encodeURIComponent(slug)}/edit-history`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async createGuide(data: { city: string; category: string; title: string }): Promise<NewGuideResult> {
    try {
      const response = await apiClient.post<NewGuideResult>('/api/v1/guides', data, {
        timeout: EDIT_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async searchPlaces(params: {
    q: string;
    city: string;
    slug?: string;
    limit?: number;
  }): Promise<PlaceSearchResult> {
    try {
      const response = await apiClient.get<PlaceSearchResult>('/api/v1/places/search', {
        params,
        timeout: SEARCH_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },
};
